import { apiDelete, apiGet, apiPost, apiPut } from './http'

export interface BugReport {
  id: number
  userId: number
  title: string
  description: string
  category:
    | 'commentaire'
    | 'probleme'
    | 'amelioration_ux'
    | 'fonctionnalite_manquante'
    | 'bug_affichage'
    | 'bug_fonctionnel'
    | 'autre'
  consoleContent: string | null
  screenshotUrl?: string
  screenshotMimeType: string | null
  url: string
  browserInfo: object | null
  priority: 'low' | 'medium' | 'high' | 'critical'
  status: 'open' | 'in_progress' | 'resolved' | 'closed'
  createdAt: string
  updatedAt: string
  resolvedAt: string | null
  resolvedBy: number | null
  resolutionComment: string | null
  author?: {
    id: number
    firstName: string
    lastName: string
    email: string
  }
}

export interface BugReportFilters {
  status?: 'open' | 'in_progress' | 'resolved' | 'closed'
  priority?: 'low' | 'medium' | 'high' | 'critical'
  category?:
    | 'commentaire'
    | 'probleme'
    | 'amelioration_ux'
    | 'fonctionnalite_manquante'
    | 'bug_affichage'
    | 'bug_fonctionnel'
    | 'autre'
  userId?: number
  search?: string
  startDate?: string
  endDate?: string
  limit?: number
  offset?: number
  sortBy?: 'createdAt' | 'priority' | 'status' | 'category'
  sortOrder?: 'asc' | 'desc'
}

export interface BugReportPaginatedResponse {
  data: BugReport[]
  meta: {
    total: number
    limit: number
    offset: number
    hasNext: boolean
    hasPrevious: boolean
  }
}

export interface CreateBugReportRequest {
  title: string
  description: string
  category?:
    | 'commentaire'
    | 'probleme'
    | 'amelioration_ux'
    | 'fonctionnalite_manquante'
    | 'bug_affichage'
    | 'bug_fonctionnel'
    | 'autre'
  consoleContent?: string | null
  screenshot?: string // base64, optionnel
  screenshotMimeType?: string
  url: string
  browserInfo?: object
  priority?: 'low' | 'medium' | 'high' | 'critical'
}

export interface UpdateBugReportRequest {
  title?: string
  description?: string
  status?: 'open' | 'in_progress' | 'resolved' | 'closed'
  priority?: 'low' | 'medium' | 'high' | 'critical'
  resolutionComment?: string
  screenshot?: string // base64 data URL
  screenshotMimeType?: string
}

export interface BugReportStats {
  open: number
  in_progress: number
  resolved: number
  closed: number
}

export const bugReportsApi = {
  /**
   * Créer une nouvelle remontée de bug
   */
  create: async (data: CreateBugReportRequest): Promise<BugReport> => {
    return apiPost<BugReport>('/bug-reports', data)
  },

  /**
   * Récupérer toutes les remontées avec filtres
   */
  list: async (filters?: BugReportFilters): Promise<BugReportPaginatedResponse> => {
    return apiGet<BugReportPaginatedResponse>(
      '/bug-reports',
      filters as Record<string, string | number | undefined>
    )
  },

  /**
   * Récupérer une remontée par ID
   */
  getById: async (id: number): Promise<BugReport> => {
    return apiGet<BugReport>(`/bug-reports/${id}`)
  },

  /**
   * Récupérer le screenshot d'une remontée (retourne l'URL blob ou null si 404)
   */
  getScreenshot: async (id: number): Promise<string | null> => {
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}/bug-reports/${id}/screenshot`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      }
    )

    // Si 404, le screenshot n'existe pas, retourner null sans erreur
    if (response.status === 404) {
      return null
    }

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération du screenshot')
    }

    const blob = await response.blob()
    return URL.createObjectURL(blob)
  },

  /**
   * Mettre à jour une remontée
   */
  update: async (id: number, data: UpdateBugReportRequest): Promise<BugReport> => {
    return apiPut<BugReport>(`/bug-reports/${id}`, data)
  },

  /**
   * Supprimer une remontée
   */
  delete: async (id: number): Promise<void> => {
    return apiDelete<void>(`/bug-reports/${id}`)
  },

  /**
   * Récupérer les statistiques
   */
  getStats: async (): Promise<BugReportStats> => {
    return apiGet<BugReportStats>('/bug-reports/stats')
  },
}
